import { lazy } from 'react'

// project-imports
import MainLayout from 'layout/MainLayout'
import CommonLayout from 'layout/CommonLayout'
import Loadable from 'components/Loadable'
import AuthGuard from 'utils/route-guard/AuthGuard'

// render - analytics
const AnalyticsProject = Loadable(lazy(() => import('pages/analytics')))
const FunnelAnalytics = Loadable(lazy(() => import('pages/analytics/funnel')))

// render - subscribers
const SubscribersPage = Loadable(lazy(() => import('pages/subscribers')))
const SendEmailPage = Loadable(lazy(() => import('pages/send-email')))
const AddSubscribersPage = Loadable(lazy(() => import('pages/subscribers/add')))

// render - posts
const PostsPage = Loadable(lazy(() => import('pages/posts')))
const PostsPageCreate = Loadable(lazy(() => import('pages/posts/create')))

// render - episodes
const EpisodesPage = Loadable(lazy(() => import('pages/episodes')))
const EpisodesPageCreate = Loadable(lazy(() => import('pages/episodes/create')))

// render - monetization
const MonetizationPage = Loadable(lazy(() => import('pages/monetization')))

// render - collectibles
const CollectiblesPage = Loadable(lazy(() => import('pages/collectibles')))
const CollectiblesCreate = Loadable(
	lazy(() => import('pages/collectibles/create'))
)

// render - campaigns
const CampaignsPage = Loadable(lazy(() => import('pages/campaigns')))
const CampaignDetailPage = Loadable(
	lazy(() => import('pages/campaigns/[id]'))
)

// render - orders
const OrdersPage = Loadable(lazy(() => import('pages/orders')))
const OrderDetailPage = Loadable(lazy(() => import('pages/orders/[id]')))

// render - wallet
const Wallet = Loadable(lazy(() => import('pages/wallet')))
const RequestWithdraw = Loadable(
	lazy(() => import('pages/wallet/requestWithdrawPage'))
)

// render - dashboard
const DashboardDefault = Loadable(lazy(() => import('pages/dashboard/home')))
const Projects = Loadable(lazy(() => import('pages/dashboard/projects/index')))
const Posts = Loadable(
	lazy(() => import('pages/dashboard/projects/item/posts/index'))
)
const ProjectPage = Loadable(
	lazy(() => import('pages/dashboard/projects/projectPage/index'))
)
const EditChapter = Loadable(
	lazy(() => import('pages/dashboard/projects/item/editChapter'))
)

// render - settings
const SettingsPage = Loadable(lazy(() => import('pages/settings')))
const CreateCoupon = Loadable(lazy(() => import('pages/coupons')))

// render - account
const AccountProfile = Loadable(
	lazy(() => import('pages/apps/profiles/account'))
)
const AccountTabStudio = Loadable(
	lazy(() => import('sections/apps/profiles/account/TabStudio'))
)
const AccountTabPartners = Loadable(
	lazy(() => import('sections/apps/profiles/account/TabPartners'))
)
const AccountTabPassword = Loadable(
	lazy(() => import('sections/apps/profiles/account/TabPassword'))
)

// render - auth + maintenance
const AuthLogin = Loadable(lazy(() => import('pages/auth/login')))
const AuthRegister = Loadable(lazy(() => import('pages/auth/register')))
const AuthForgotPassword = Loadable(
	lazy(() => import('pages/auth/forgot-password'))
)
const AuthResetPassword = Loadable(
	lazy(() => import('pages/auth/reset-password'))
)
const AuthCheckMail = Loadable(lazy(() => import('pages/auth/check-mail')))
const AuthCodeVerification = Loadable(
	lazy(() => import('pages/auth/code-verification'))
)

const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')))
const MaintenanceError500 = Loadable(
	lazy(() => import('pages/maintenance/500'))
)
const MaintenanceUnderConstruction = Loadable(
	lazy(() => import('pages/maintenance/under-construction'))
)
const MaintenanceComingSoon = Loadable(
	lazy(() => import('pages/maintenance/coming-soon'))
)

// ==============================|| MAIN ROUTES ||============================== //

const MainRoutes = {
	path: '/',
	children: [
		{
			path: '/',
			element: (
				<AuthGuard>
					<MainLayout />
				</AuthGuard>
			),
			children: [
				{ path: '/analytics/funnel', element: <FunnelAnalytics /> },
				{ path: '/analytics/:id', element: <AnalyticsProject /> },
				{ path: '/subscribers', element: <SubscribersPage /> },
				{ path: '/subscribers/add', element: <AddSubscribersPage /> },
				{ path: '/send-email', element: <SendEmailPage /> },
				{ path: '/settings', element: <SettingsPage /> },
				{ path: '/wallet', element: <Wallet /> },
				{ path: '/wallet/request-withdraw', element: <RequestWithdraw /> },
				{ path: '/series', element: <Projects /> },
				{
					path: '/series/chapter/edit/:projectId/:id',
					element: <EditChapter />,
				},
				{ path: '/series/:id', element: <ProjectPage /> },
				{ path: '/series/create', element: <ProjectPage /> },
				{ path: '/posts/:id', element: <PostsPage /> },
				{ path: '/posts/create/:id', element: <PostsPageCreate /> },
				{ path: '/posts', element: <Posts /> },
				{ path: '/episodes/:id', element: <EpisodesPage /> },
				{ path: '/episodes/create/:id', element: <EpisodesPageCreate /> },
				{
					path: '/episodes/edit/:id/:episodeId',
					element: <EpisodesPageCreate />,
				},
				{ path: '/monetization/:id', element: <MonetizationPage /> },
				{ path: '/collectibles', element: <CollectiblesPage /> },
				{ path: '/collectibles/create', element: <CollectiblesCreate /> },
				{ path: '/collectibles/:id', element: <CollectiblesCreate /> },
				{ path: '/campaigns', element: <CampaignsPage /> },
				{ path: '/campaigns/:id', element: <CampaignDetailPage /> },
				{ path: '/orders', element: <OrdersPage /> },
				{ path: '/orders/:id', element: <OrderDetailPage /> },
				{
					path: 'dashboard',
					children: [
						{ path: 'home', element: <DashboardDefault /> },
						{ path: 'coupons', element: <CreateCoupon /> },
					],
				},
				{
					path: 'coupons',
					children: [{ path: 'create', element: <CreateCoupon /> }],
				},
				{
					path: 'apps',
					children: [
						{
							path: 'profiles',
							children: [
								{
									path: 'account',
									element: <AccountProfile />,
									children: [
										{ path: 'basic', element: <AccountTabStudio /> },
										{ path: 'partners', element: <AccountTabPartners /> },
										{ path: 'password', element: <AccountTabPassword /> },
									],
								},
							],
						},
					],
				},
			],
		},
		{
			path: '/maintenance',
			element: <CommonLayout />,
			children: [
				{ path: '404', element: <MaintenanceError /> },
				{ path: '500', element: <MaintenanceError500 /> },
				{ path: 'under-construction', element: <MaintenanceUnderConstruction /> },
				{ path: 'coming-soon', element: <MaintenanceComingSoon /> },
			],
		},
		{
			path: '/auth',
			element: <CommonLayout />,
			children: [
				{ path: 'login', element: <AuthLogin /> },
				{ path: 'register', element: <AuthRegister /> },
				{ path: 'forgot-password', element: <AuthForgotPassword /> },
				{ path: 'reset-password', element: <AuthResetPassword /> },
				{ path: 'check-mail', element: <AuthCheckMail /> },
				{ path: 'code-verification', element: <AuthCodeVerification /> },
			],
		},
	],
}

export default MainRoutes
